import {
  formatUnit,
  isValidAddress,
  pickNumberResponse,
  rpcContractCall,
  useWallet,
} from '@bifrost-platform/bifront-sdk-react-wallet';
import { useAtom } from 'jotai';
import { useCallback } from 'react';
import { FRAG_LP_REWARD_EARNED } from '@/configs/fragment';
import useEnv from '@/hooks/useEnv';
import useIsBifrostChain from '@/hooks/useIsBifrostChain';
import { formatNumber } from '@/libs/formatNumber';
import {
  everdexLpRewardAmountAtom,
  everdexIsLoadingLpRewardAmountAtom,
} from '@/store/everdexStore';

const useEverdexLpRewardAmount = (
  proxyAddress?: string,
  rewardStateAddress?: string
) => {
  const { chainBifrost } = useEnv();

  const { wallet, account } = useWallet();
  const isBifrostChain = useIsBifrostChain();

  const [rewardAmount, setRewardAmount] = useAtom(everdexLpRewardAmountAtom);
  const [isLoading, setIsLoading] = useAtom(everdexIsLoadingLpRewardAmountAtom);

  const sync = useCallback(async () => {
    if (!(proxyAddress && isValidAddress(proxyAddress))) {
      return;
    }

    setIsLoading(true);

    try {
      const to = proxyAddress;
      const fragment = FRAG_LP_REWARD_EARNED;
      const values: ReadonlyArray<any> = [rewardStateAddress, account];
      const from = account;
      const defaultBlock = 'latest';

      let rewardAmount = '0';

      if (isBifrostChain) {
        rewardAmount = pickNumberResponse(
          await wallet.call(to, fragment, values, from, defaultBlock)
        );
      } else {
        const rpcURL = chainBifrost.rpcUrls[0];
        const callParams = {
          from,
          to,
          signature: fragment,
          values,
        };

        rewardAmount = pickNumberResponse(
          await rpcContractCall(callParams, rpcURL, defaultBlock)
        );
      }

      setRewardAmount(formatNumber(formatUnit(rewardAmount, 18)));
    } catch (error) {}

    setIsLoading(false);
  }, [
    account,
    chainBifrost.rpcUrls,
    isBifrostChain,
    proxyAddress,
    rewardStateAddress,
    setIsLoading,
    setRewardAmount,
    wallet,
  ]);

  return { amount: rewardAmount, isLoading, sync };
};

export default useEverdexLpRewardAmount;
